/**
 * Resend a booking's confirmation email, with an optional correction of the
 * customer's email on file (BON-630).
 *
 * STRUCTURE FOLLOWS `EditReservationContactDialog`: a form, `noValidate` (this
 * codebase's own inline error replaces the browser's unstyled bubble), and a
 * failure that stays inline rather than closing the dialog — see
 * `useResendConfirmationForm` for exactly which failures that is.
 *
 * THE WARNING IS THE NEW-EMAIL INPUT'S OWN DESCRIPTION (`aria-describedby` on
 * the input itself, via `LabeledTextInput`'s `describedBy`), not the dialog's —
 * a dialog-level `aria-describedby` is announced once, on open, before the
 * field it is about even exists; the field's own describedby is announced
 * every time focus actually lands there. There is deliberately no
 * dialog-level `aria-describedby` alongside it, so the warning is not
 * announced twice.
 *
 * FOCUS OPENS ON CANCEL (contract), not a field — a stray Enter on open must
 * not fire a send whose warning nobody read yet.
 *
 * `dialogHeader` and `correctionSection` are plain (lowercase) functions, not
 * components, same reasoning as `CancelBookingDialog`'s `dialogHeader` /
 * `reasonField`: each is called exactly once, and splitting the markup out is
 * what keeps the dialog's own function under the lint size caps.
 */
import { useRef } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';
import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { DialogFooterActions } from '../../shared/DialogFooterActions';
import { LabeledTextInput } from '../../shared/LabeledTextInput';

import type { ResendConfirmationOutcome } from './resend-confirmation-outcome';
import { useResendConfirmationForm } from './use-resend-confirmation-form';

const TITLE_ID = 'resend-confirmation-title';
const WARNING_ID = 'resend-confirmation-warning';
const ERROR_ID = 'resend-confirmation-error';

function dialogHeader(booking: BookingDetail) {
  return (
    <div className="border-b border-stone-200 px-6 py-4 dark:border-stone-700">
      <h2 id={TITLE_ID} className="text-lg font-bold text-bark-900 dark:text-stone-100">
        Resend confirmation
      </h2>
      <p className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
        {booking.confirmationCode ?? booking.id}
      </p>
    </div>
  );
}

/** Same visible/hover style as every other secondary text button in this
 *  dialog family, with a real tap target and a focus ring added (BON-630
 *  round 2): a bare underlined inline-text link is too small to tap reliably,
 *  and had no visible focus state for a keyboard user tabbing through. */
const TOGGLE_CLS =
  'inline-flex min-h-[44px] items-center rounded-lg px-2 py-2 text-sm font-medium text-moss-600 ' +
  'hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-moss-500 ' +
  'dark:text-moss-400';

function correctionSection(form: ReturnType<typeof useResendConfirmationForm>) {
  const describedBy = [form.correcting ? WARNING_ID : null, form.emailInvalid ? ERROR_ID : null]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {!form.forceCorrection && (
        <button type="button" onClick={form.toggleCorrecting} className={TOGGLE_CLS}>
          {form.correcting ? 'Use the email on file' : 'Correct the email on file'}
        </button>
      )}

      {form.correcting && (
        <div className="space-y-2">
          <p
            id={WARNING_ID}
            className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
          >
            This changes the customer&rsquo;s email on file for all their bookings and future
            emails.
          </p>
          <LabeledTextInput
            id="resend-confirmation-email"
            label="New email"
            type="email"
            value={form.email}
            onChange={form.setEmail}
            required
            describedBy={describedBy || undefined}
            invalid={form.emailInvalid}
          />
        </div>
      )}
    </>
  );
}

export function ResendConfirmationDialog({
  booking,
  onClose,
  onDone,
  onRefresh,
}: {
  booking: BookingDetail;
  onClose: () => void;
  /** Fired for a send that settled (sent, or failed outright) — the caller
   *  closes the dialog and owns the toast/alert and the refresh. */
  onDone: (outcome: ResendConfirmationOutcome) => void;
  /** Re-read the booking without closing the dialog, for the one refusal
   *  whose booking is already stale (`BOOKING_NOT_CONFIRMED`). */
  onRefresh: () => void;
}) {
  const form = useResendConfirmationForm(booking, onDone, onRefresh);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useDialogKeyboard(onClose, cancelRef, form.sending);

  // Escape, Cancel and the backdrop all go inert while the send is out — same
  // rule as every other BON-472/BON-630 dialog: nothing here can call it back.
  const dismiss = () => {
    if (!form.sending) onClose();
  };

  return (
    <div
      role="presentation"
      onClick={dismiss}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={form.submit}
        noValidate
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1a1f1c]"
      >
        {dialogHeader(booking)}

        <div className="space-y-4 px-6 py-5">
          <div>
            <span className="block text-sm font-semibold text-bark-900 dark:text-stone-100">
              Email on file
            </span>
            <p className="mt-1 text-sm text-stone-700 dark:text-stone-300">
              {form.emailOnFile ?? 'No email on file'}
            </p>
          </div>

          {correctionSection(form)}

          {form.error && (
            <p id={ERROR_ID} role="alert" className="text-sm text-red-600 dark:text-red-400">
              {form.error}
            </p>
          )}
        </div>

        <DialogFooterActions
          onCancel={dismiss}
          cancelRef={cancelRef}
          confirmLabel="Send"
          confirmBusyLabel="Sending…"
          busy={form.sending}
        />
      </form>
    </div>
  );
}
